// src/modules/app-views/_apps/appmulti/receivables/config.js
// *******************************************************************************
// IMPORTS
// *******************************************************************************
import ReceivablesInit from './pages/Init';
import ListProvider from 'components/core/ListProvider';
import { API } from 'constants/Api';
import receivablesInitActionButton from './pages/Init/actions/actionButton';
import receivablesInitMenuButton from './pages/Init/actions/menuButton';
import DocumentProvider from 'components/core/DocumentProvider';
import ReceivablesDocument from './pages/Document';
import receivablesDocumentActionButton from './pages/Document/actions/actionButton';
import receivablesDocumentDropdownMenu from './pages/Document/actions/dropdownMenu';
import receivablesDocumentMenuButton from './pages/Document/actions/menuButton';
import receivablesDocumentDecorateRequest from './pages/Document/decorators/request';
import receivablesDocumentDecorateResponse from './pages/Document/decorators/response';
import CardHeaderTitle from 'layouts/Pages/PageHeader/CardHeaderTitle';
import CardHeaderSubtitle from 'layouts/Pages/PageHeader/CardHeaderSubtitle';
import CardHeaderColumn from 'layouts/Pages/PageHeader/CardHeaderColumn';
import {
  createdAtItem,
  updatedAtItem,
} from 'layouts/Pages/PageHeader/CardHeaderColumn/items';
import seedReceivablesParameters from './seed';
import ReceivablesPreviewHeader from './pages/Init/components/ReceivablesPreviewHeader';
import ReceivablesPreviewActions from './pages/Init/components/ReceivablesPreviewActions';
import CardHeaderIcons from 'layouts/Pages/PageHeader/CardHeaderIcons';
import {
  receivableCustomerItem,
  receivableDueDateItem,
  receivableOrderItem,
  receivablePaymentLinkItem,
  receivableStatusItem,
  receivableValueItem,
} from './pages/Document/cardHeaderItems';

// *******************************************************************************
// CONSTANTS
// *******************************************************************************
const MODULE_ID = 'receivables';

// *******************************************************************************
// PRE CONFIG
// *******************************************************************************
const preConfigChanges = appId => {
  CONFIG.appId = appId;
};

// *******************************************************************************
// MODULE CONFIGURATION
// *******************************************************************************
const CONFIG = {
  version: 2,
  id: MODULE_ID,
  initialPage: 'init',
  initialParams: {},
  initialQuery: {},
  parametersModalComponent: null,
  parametersSeed: seedReceivablesParameters,

  migratory: {
    title: 'receivables_migration',
    schema: [],
  },
};

// *******************************************************************************
// MODULE PAGES
// *******************************************************************************
const PAGES = {
  init: {
    layout: 'list',
    moduleId: MODULE_ID,
    component: props => (
      <ListProvider {...props}>
        <ReceivablesInit />
      </ListProvider>
    ),

    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: { br: null, us: null },
    },

    api: {
      url: API.RECEIVABLES.PAGINATION,
      params: null,
      query: null,
      returnAsObject: false,
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    decorators: { request: null, response: null },

    routing: { endpoint: null, paramKey: null, query: null },

    pagination: {
      pagination: { page: 1, size: 10 },
      sort: [{ field: '_metadata.audit.updatedAt', order: 'desc', index: 1 }],
      search: {},
      textSearch: '',
      filter: {},
      drawer: { folder: 'inbox' },
    },

    table: {
      actions: ['edit', 'archive', 'remove'],
      hiddenColumns: ['createdAt'],
    },

    settings: null,

    preview: {
      actions: ['edit', 'archive', 'remove'],
      showCardHeader: false,
      noDividerAfterHeader: true,
      components: {
        title: 'charge',
        header: props => <ReceivablesPreviewHeader {...props} />,
        body: null,
        actions: props => <ReceivablesPreviewActions {...props} />,
        footer: null,
      },
    },

    header: {
      visible: true,
      style: 'default',
      // refs WITHOUT () — golden visual contract (init action button anti-pattern)
      actions: ['filter', 'refresh', receivablesInitActionButton, receivablesInitMenuButton],
      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['financial', 'charges'],
      },
    },
  },

  document: {
    highSecurity: false,

    layout: 'document',
    moduleId: MODULE_ID,
    component: props => (
      <DocumentProvider {...props}>
        <ReceivablesDocument />
      </DocumentProvider>
    ),

    tour: {
      firstModal: null,
      lastModal: null,
      onFinish: null,
      triggerConfettiOnFinish: false,
      videoLink: { br: null, us: null },
    },

    api: {
      url: API.RECEIVABLES.URL,
      params: null,
      query: null,
      returnAsObject: true,
      autoFetch: true,
      fetchWithoutSubscription: false,
      removeSubscriptionIdFromHeader: false,
      persistInStore: true,
    },

    decorators: {
      request: receivablesDocumentDecorateRequest,
      response: receivablesDocumentDecorateResponse,
    },

    routing: { endpoint: '/:_id', paramKey: '_id', query: null },

    pagination: null,
    table: null,

    settings: {
      initialTab: 'record',
      initialValues: {
        status: 'new',
        billingType: 'whatever',
        installment: 1,
      },
      actions: [
        'editRefresh',
        receivablesDocumentActionButton(),
        receivablesDocumentMenuButton(),
        receivablesDocumentDropdownMenu(),
        'return',
      ],
      viewMode: 'auto',
    },

    header: {
      visible: true,
      style: 'card',
      avatar: { visible: true, placeholder: 'file-text' },
      navigation: {
        showTitle: true,
        showBreadcrumb: true,
        customBreadcrumb: () => ['financial', 'charges'],
      },
      card: {
        title: props => <CardHeaderTitle {...props} placeholder="new_charge" />,
        subtitle: props => <CardHeaderSubtitle {...props} />,
        column1: props => (
          <CardHeaderColumn
            {...props}
            items={[
              receivableStatusItem,
              receivableValueItem,
              receivableDueDateItem,
              receivablePaymentLinkItem,
            ]}
          />
        ),
        column2: props => (
          <CardHeaderColumn
            {...props}
            items={[receivableCustomerItem, receivableOrderItem, createdAtItem, updatedAtItem]}
          />
        ),
        icons: (data, countryCode) => <CardHeaderIcons data={data} countryCode={countryCode} />,
      },
    },
  },
};

// *******************************************************************************
// EXPORT
// *******************************************************************************
export default { CONFIG, PAGES, preConfigChanges };
